import type {HeaderResponse} from '@github-ui/code-view-header/types'
import {BranchAndTagCountProvider} from '@github-ui/code-view-shared/contexts/BranchAndTagContext'
import {CommitInfoProvider} from '@github-ui/code-view-shared/contexts/CommitInfoContext'
import {LatestCommitProvider} from '@github-ui/code-view-shared/contexts/LatestCommitContext'
import {RecentlyTouchedBranchesProvider} from '@github-ui/code-view-shared/contexts/RecentlyTouchedBranchesContext'
import {type DataRouterFileOverviewPayload, isErrorPayload} from '@github-ui/code-view-types'
import {useCurrentRepository} from '@github-ui/current-repository'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {queryFnFetch} from '@github-ui/react-core/future/query-fn-fetch'
import {useRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {useQuery} from '@github-ui/react-query'

import type {MergedHeaderPayload} from '../components/overview/OverviewHeader'
import {SharedPageLayout} from '../components/SharedPageLayout'
import {codeViewRepoRoute} from '../routes/code-view-repo-route'
import {CodeViewRepoErrorPage} from './CodeViewRepoErrorPage'
import {ReposOverviewPage} from './ReposOverviewPage'

export function CodeViewRepoEntrypoint() {
  const {data: payload} = useRouteQuery(codeViewRepoRoute, 'mainQuery')

  if (isErrorPayload(payload)) {
    return <CodeViewRepoErrorPage payload={payload} />
  }

  return <CodeViewRepoInternal payload={payload} />
}

function CodeViewRepoInternal({payload}: {payload: DataRouterFileOverviewPayload}) {
  const {
    data: latestCommit,
    error: latestCommitError,
    isLoading: latestCommitLoading,
  } = useRouteQuery(codeViewRepoRoute, 'latestCommit')
  const {
    data: commitInfo,
    error: commitInfoError,
    isLoading: commitInfoLoading,
  } = useRouteQuery(codeViewRepoRoute, 'treeCommitInfo')
  const {data: recentlyTouchedBranches, refetch: refetchBranches} = useRouteQuery(
    codeViewRepoRoute,
    'recentlyTouchedBranches',
  )
  const {data: branchAndTagCount} = useRouteQuery(codeViewRepoRoute, 'branchAndTagCount')
  const {data: sidebarAboutFromRouteQuery} = useRouteQuery(codeViewRepoRoute, 'sidebarAbout')

  // With the flag on, the sidebarAbout route query is disabled and the main query asks the server to
  // embed the data in the main payload instead (matching the tree route). Wrap it in useQuery so it has a queryKey for cache
  // invalidation, and fall back to fetching client-side if the embedded data is missing.
  const embedSidebarAbout = isFeatureEnabled('code_view_embed_overview_sidebar_about')
  const repo = useCurrentRepository()
  const sidebarAboutQueryDeps = {pathname: `/${repo.ownerLogin}/${repo.name}/_sidebar/about`}
  const {data: embeddedSidebarAbout} = useQuery({
    queryKey: [repo.ownerLogin, repo.name, 'sidebarAbout', sidebarAboutQueryDeps] as const,
    queryFn: () => queryFnFetch<HeaderResponse>({queryDeps: sidebarAboutQueryDeps}),
    initialData: embedSidebarAbout ? payload.overview.sidebarAboutData : undefined,
    enabled: embedSidebarAbout,
    staleTime: Infinity,
  })

  const sidebarPayload: MergedHeaderPayload = {
    aboutData: embedSidebarAbout ? embeddedSidebarAbout : sidebarAboutFromRouteQuery,
  }

  return (
    <BranchAndTagCountProvider count={branchAndTagCount}>
      <RecentlyTouchedBranchesProvider {...recentlyTouchedBranches} refetch={refetchBranches}>
        <SharedPageLayout narrow={false}>
          <CommitInfoProvider commitInfo={commitInfo} error={!!commitInfoError} loading={commitInfoLoading}>
            <LatestCommitProvider latestCommit={latestCommit} error={latestCommitError} isLoading={latestCommitLoading}>
              <ReposOverviewPage payload={payload} sidebarPayload={sidebarPayload} />
            </LatestCommitProvider>
          </CommitInfoProvider>
        </SharedPageLayout>
      </RecentlyTouchedBranchesProvider>
    </BranchAndTagCountProvider>
  )
}
